/**
 * Use the Compact Promotional Group to feature 5+ promoted stories in a compact way that are related. For example, Beauty steals during a major event. Use when a higher prominence and/or creative image is needed.
 *
 * @module views/components/PromoGroup
 * @memberof -Common
 */
import './PromoGroup.scss';

import React from 'react';

import PropTypes from 'prop-types';

import { hasItems } from '@ulta/core/utils/array/array';

import Titles from '@ulta/modules/Titles/Titles';

import PromotionalRailGrid from '../PromotionalRailGrid/PromotionalRailGrid';
import * as utils from './PromoGroup';

/**
 * Represents a PromoGroup component
 *
 * @method
 * @param {object} props - React properties passed from composition. See PromoGroupProps.
 * @returns PromoGroup
 */
export const PromoGroup = function( props ){
  const {
    modules,
    railADAMeta,
    railMeta,
    title = {},
    countLabel,
    isGrid,
    previousClickAction,
    nextClickAction,
    salePriceAccessibilityLabel,
    listPriceAccessibilityLabel
  } = props;

  const {
    headline,
    subheadline,
    headlineTag = 'h2',
    linkAction,
    textAlignment
  } = title;

  if( !hasItems( modules ) ){
    return null;
  }
  return (
    <div className='PromoGroup'>
      <div className='PromoGroup__content'>
        { headline &&
          <div className='PromoGroup__titleSection'>
            <Titles
              headline={ headline }
              subheadline={ subheadline }
              headlineTag={ headlineTag }
              linkAction={ linkAction }
              textAlignment={ textAlignment }
              countLabel={ countLabel }
            />
          </div>
        }
      </div>
      <PromotionalRailGrid
        items={ modules }
        isGrid={ isGrid }
        previousClickAction={ previousClickAction }
        nextClickAction={ nextClickAction }
        _railADAmeta={ railADAMeta }
        _railMeta={ railMeta }
        sliderConfig={ utils.SLIDER_CONFIG }
        salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
        listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
      />
    </div>
  );
};

/**
 * Configuration for the slider/carousel behavior in PromotionalRailGrid.
 * It defines how many slides are visible and how many slides to group for navigation
 * at different breakpoints (XL, LG, MD, SM).
 *
 * @const
 * @type {object}
 * @property {number} sliderPerViewXL - Number of slides to show on extra-large screens.
 * @property {number} slidesPerGroupXL - Number of slides to scroll on extra-large screens.
 * @property {number} sliderPerViewLG - Number of slides to show on large screens.
 * @property {number} slidesPerGroupLG - Number of slides to scroll on large screens.
 * @property {number} sliderPerViewMD - Number of slides to show on medium screens.
 * @property {number} slidesPerGroupMD - Number of slides to scroll on medium screens.
 * @property {number} sliderPerViewSM - Number of slides to show on small screens.
 * @property {number} slidesPerGroupSM - Number of slides to scroll on small screens.
 */
export const SLIDER_CONFIG = {
  sliderPerViewXL: 6,
  slidesPerGroupXL: 6,
  sliderPerViewLG: 4,
  slidesPerGroupLG: 4,
  sliderPerViewMD: 4,
  slidesPerGroupMD: 4,
  sliderPerViewSM: 1.5,
  slidesPerGroupSM: 1
};

/**
 * PromoGroup Variants
 */
export const PROMO_GROUP_VARIANT = {
  GRID: 'grid',
  RAIL: 'rail'
};

/**
 * PromoGroup Min Heights for loading states
 * These are the default values set in component design on figma
 * The min-heights exported here are for default grid and default rail.
 */
export const PROMO_GROUP_MIN_HEIGHT = {
  [PROMO_GROUP_VARIANT.GRID]: {
    xl: '18.875rem', // 302px
    lg: '13.688rem', // 219px
    md: '10.75rem', // 172px
    sm: '10.563rem' // 169px
  },
  [PROMO_GROUP_VARIANT.RAIL]: {
    xl: '13.813rem', // 221px
    lg: '14.313rem', // 229px
    md: '11.25rem', // 180px
    sm: '14rem' // 280px
  }
};

/**
 * Property type definitions
 *
 * @typedef PromoGroupProps
 * @type {object}
 * @property {object} title - Sets the title properties.
 * @property {array} modules - Set the module array.
 * @property {string} countLabel - Sets the count label.
 * @property {boolean} isGrid - sets the value true/false based on value.
 * @property {object} previousClickAction - The prop that holds the action for the previous button.
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {object} railADAmeta - sets carouselAccessibility.
 * @property {object} railMeta - sets nextAccessibility, previousAccessibility.
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label for screen readers
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label for screen readers
 */

export const propTypes = {
  title: PropTypes.shape( {
    headline: PropTypes.string,
    subheadline: PropTypes.string,
    headlineTag: PropTypes.string,
    linkAction: PropTypes.object,
    textAlignment: PropTypes.string
  } ),
  countLabel: PropTypes.string,
  modules: PropTypes.array,
  isGrid: PropTypes.bool,
  railADAmeta: PropTypes.object,
  railMeta: PropTypes.object,
  previousClickAction: PropTypes.object,
  nextClickAction: PropTypes.object,
  listPriceAccessibilityLabel: PropTypes.string,
  salePriceAccessibilityLabel: PropTypes.string
};

PromoGroup.propTypes = propTypes;
PromoGroup.minHeights = PROMO_GROUP_MIN_HEIGHT;

export default PromoGroup;